import type { AxiosError } from 'axios';

export interface UploadErrorPayload {
  detail?: string;
}

const MESSAGES = {
  requestTooLarge:
    'アップロードするデータのサイズが上限を超えています。ファイルを分割して再度お試しください。',
  authenticationRequired:
    'ログインの有効期限が切れています。再ログインしてからアップロードしてください。',
  emailVerificationRequired:
    'メール認証が完了していないためアップロードできません。認証を完了してから再度お試しください。',
  bankTransferGrace:
    '銀行振込の入金待ち期間中はアップロードできません。入金確認後に再度お試しください。',
  forbidden:
    '現在のアカウントではアップロードできません。契約状態または権限を確認してください。',
  invalidRequest:
    'ファイルの形式または入力内容を確認して、再度アップロードしてください。',
  server:
    'サーバー側でアップロード処理に失敗しました。時間をおいて再度お試しください。',
  network:
    'アップロード先に接続できませんでした。ネットワーク接続を確認して再度お試しください。',
} as const;

const DETAIL_MESSAGES: Record<string, string> = {
  request_too_large: MESSAGES.requestTooLarge,
  email_verification_required: MESSAGES.emailVerificationRequired,
  upload_forbidden_during_bank_transfer_grace: MESSAGES.bankTransferGrace,
  forbidden: MESSAGES.forbidden,
};

export const getUploadErrorMessage = (
  error: AxiosError<UploadErrorPayload>,
): string => {
  const status = error.response?.status;
  const detail = error.response?.data?.detail;

  if (detail && DETAIL_MESSAGES[detail]) {
    return DETAIL_MESSAGES[detail];
  }
  if (typeof detail === 'string' && /[ぁ-んァ-ヶ一-龠]/u.test(detail)) {
    return detail;
  }
  if (!error.response) {
    return MESSAGES.network;
  }
  if (status === 413) {
    return MESSAGES.requestTooLarge;
  }
  if (status === 401) {
    return MESSAGES.authenticationRequired;
  }
  if (status === 403) {
    return MESSAGES.forbidden;
  }
  if (status === 400 || status === 422) {
    return MESSAGES.invalidRequest;
  }
  if (status && status >= 500) {
    return MESSAGES.server;
  }
  return `アップロードに失敗しました（HTTP ${status ?? 'unknown'}）。時間をおいて再度お試しください。`;
};
